import type {PaymentCard} from '@yandex-delivery/api/model/payment'
import type {NotificationsAllow} from '@yandex-delivery/api/service/payment/types'
import {useAddNotification, useTransition} from '@yandex-delivery/blocks'
import {delay} from '@yandex-delivery/utils'

import type {Props} from './BoundCardModal.types'
import {buildKey} from './utils'

type BoundCardResponse = [PaymentCard[]?, string?, NotificationsAllow?]

export function useBoundCardModal(props: Props) {
  const {
    fetchCards,
    fetchAgreement,
    fetchNotificationsAgreements,
    fetchBoundCard,
    fetchCardBoundContext,
    onClose,
    onSuccess,
    card,
    successNotificationText,
  } = props
  const addNotification = useAddNotification()
  const [{isPending, payload, error}, startTransition] = useTransition<BoundCardResponse, unknown>(
    (): Promise<BoundCardResponse> => Promise.all([fetchCards(), fetchAgreement?.(), fetchNotificationsAgreements?.()]),
    {key: buildKey('list')},
  )
  const [cards, agreement, notificationsPermission] = payload || []
  // примитивный механизм для обновление MobileDrawer, чтобы шторка подстраивалась под высоту динамического контента
  const key = (function foldAsyncState() {
    if (isPending) {
      return 'pending'
    }

    if (cards) {
      return `cards-${cards.length}`
    }

    if (error) {
      return 'error'
    }

    return 'initial'
  })()

  function successHandler(card: PaymentCard) {
    onSuccess?.(card)
    onClose?.()

    if (successNotificationText) {
      addNotification(successNotificationText, {
        type: 'success',
      })
    }
  }

  function updateHandler() {
    // после добавления карты они прорастают не сразу, надо чуть чуть подождать
    startTransition(() =>
      delay(1000).then(() => Promise.all([fetchCards(), agreement, fetchNotificationsAgreements?.()])),
    )
  }

  return {
    key,
    isPending,
    error,
    cards,
    card,
    agreement,
    isNotificationsInteracted: notificationsPermission?.is_marketing_notifications_allowed !== undefined,
    fetchBoundCard,
    fetchCardBoundContext,
    successHandler,
    updateHandler,
  } as const
}
